import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faTimes, faMinus, faPlus, faUndo } from '@fortawesome/free-solid-svg-icons';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const { cartItems, removedItem, removeFromCart, undoRemove, updateQuantity, clearCart } = useCart();
  const [showUndo, setShowUndo] = useState(false);
  
  // Show undo notification when an item is removed
  useEffect(() => {
    if (removedItem) {
      setShowUndo(true);
    } else {
      setShowUndo(false);
    }
  }, [removedItem]);

  // Generate Amazon checkout URL with cart items
  const generateAmazonCheckoutUrl = () => {
    if (cartItems.length === 0) return '';

    const baseUrl = 'https://www.amazon.com/gp/aws/cart/add.html';
    
    // Get Associate Tag from .env if available
    const associateTag = process.env.REACT_APP_AMAZON_ASSOCIATE_TAG || 'goodcart-20';
    
    // Build URL parameters
    const params = new URLSearchParams();
    if (associateTag) {
      params.append('AssociateTag', associateTag);
    }
    
    // Add each item to the URL parameters
    cartItems.forEach((item, index) => {
      params.append(`ASIN.${index + 1}`, item.asin);
      params.append(`Quantity.${index + 1}`, item.quantity.toString());
    });
    
    return `${baseUrl}?${params.toString()}`;
  };

  // Handle checkout
  const handleCheckout = () => {
    window.open(generateAmazonCheckoutUrl(), '_blank');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5, transition: { duration: 0.15 } }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            className="fixed inset-0 bg-black z-40"
            onClick={onClose}
          />
          
          {/* Cart Panel */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.15 } }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            className="fixed inset-0 bg-white z-50 overflow-y-auto flex flex-col"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white shadow-sm z-10">
              <div className="flex items-center justify-between p-4 border-b border-earth-100">
                <div className="flex items-center space-x-2">
                  <div className="text-brand-green-600">
                    <FontAwesomeIcon icon={faShoppingCart} className="h-6 w-6" />
                  </div>
                  <span className="text-xl font-display font-bold text-earth-900">GoodCart</span>
                </div>
                
                <button 
                  onClick={onClose}
                  className="p-2 text-earth-500 hover:text-earth-700 focus:outline-none"
                  aria-label="Close cart"
                >
                  <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className={`p-4 flex-1 overflow-y-auto ${cartItems.length > 0 ? 'pb-32' : 'pb-4'}`}>
              {cartItems.length === 0 ? (
                <div className="text-center py-12">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-16 w-16 mx-auto text-earth-400 mb-4"
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" 
                    />
                  </svg>
                  <h3 className="text-lg font-semibold text-earth-700 mb-4">Your cart is empty</h3>
                  <p className="text-earth-600 mb-6">Looks like you haven't added any products to your cart yet.</p>
                  <Link 
                    to="/search" 
                    className="btn btn-primary"
                    onClick={onClose}
                  >
                    Browse Products
                  </Link>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <div className="text-lg font-display font-semibold text-earth-800">
                      {cartItems.length} {cartItems.length === 1 ? 'Item' : 'Items'}
                    </div>
                    <button 
                      onClick={clearCart}
                      className="text-sm text-red-600 hover:text-red-800 font-medium transition-colors duration-200"
                    >
                      Clear Cart
                    </button>
                  </div>
                  
                  <ul className="divide-y divide-earth-100 mb-6">
                    {cartItems.map((item) => (
                      <motion.li 
                        key={item.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="py-4"
                      >
                        <div className="flex items-start">
                          <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border border-earth-100 mr-4">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="h-full w-full object-cover object-center"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = `https://placehold.co/200x200@3x?text=${item.name.replace(/ /g, '+')}&font=poppins`;
                              }}
                            />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex justify-between">
                              <h3 className="text-base font-display font-medium text-earth-900 mb-1">
                                <Link 
                                  to={`/products/${item.id}`} 
                                  className="hover:text-brand-green-600"
                                  onClick={onClose}
                                >
                                  {item.name}
                                </Link>
                              </h3>
                              <button
                                type="button"
                                onClick={() => removeFromCart(item.id)}
                                className="text-earth-400 hover:text-red-500 transition-colors duration-200"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </button>
                            </div>
                            
                            <p className="text-sm text-earth-500 mb-2">ASIN: {item.asin}</p>
                            
                            <div className="flex items-center">
                              <label htmlFor={`quantity-${item.id}`} className="text-sm font-medium text-earth-700 mr-3">
                                Quantity:
                              </label>
                              <div className="flex items-center border border-earth-200 rounded-md">
                                <button
                                  type="button"
                                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                  className="p-2 text-earth-600 hover:text-earth-800"
                                >
                                  <FontAwesomeIcon icon={faMinus} className="h-4 w-4" />
                                </button>
                                <input
                                  id={`quantity-${item.id}`}
                                  name={`quantity-${item.id}`}
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                                  className="w-12 text-center border-0 focus:ring-0"
                                />
                                <button
                                  type="button"
                                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                  className="p-2 text-earth-600 hover:text-earth-800"
                                >
                                  <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.li>
                    ))}
                  </ul>
                  
                  <div className="border-t border-earth-100 pt-4 mb-6">
                    <div className="flex justify-between text-base font-medium text-earth-900 mb-2">
                      <p>Total</p>
                      <p>Calculated at checkout</p>
                    </div>
                    <p className="text-sm text-earth-500">
                      Prices and shipping costs are calculated at checkout.
                    </p>
                  </div>                  
                </>
              )}
            </div>

            {/* Undo Notification */}
            <AnimatePresence>
              {showUndo && (
                <motion.div 
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 50 }}
                  transition={{ duration: 0.3 }}
                  className="fixed bottom-32 left-0 right-0 mx-auto w-11/12 max-w-md bg-earth-800 text-white p-4 rounded-lg shadow-lg z-20"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="mr-2">Item removed from cart</span>
                    </div>
                    <button
                      onClick={undoRemove}
                      className="flex items-center text-brand-green-400 hover:text-brand-green-300 transition-colors duration-200"
                    >
                      <FontAwesomeIcon icon={faUndo} className="mr-1 h-4 w-4" />
                      <span>Undo</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Fixed Checkout Buttons */}
            {cartItems.length > 0 && (
              <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-earth-100 p-4 shadow-lg z-10">
                <button
                  type="button"
                  onClick={handleCheckout}
                  className="w-full btn btn-primary flex items-center justify-center font-medium shadow-md hover:shadow-lg transition-shadow mb-4"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  Proceed to checkout
                </button>
                
                <Link 
                  to="/search" 
                  className="w-full btn btn-outline flex items-center justify-center font-medium"
                  onClick={onClose}
                >
                  Continue Shopping
                </Link>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;